var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Box, Stack, Typography } from "@mui/material";
import { useMemo } from "react";
import { createPortal } from "react-dom";
import { contrastText } from "./color";
var ROW_BG = "rgba(15, 23, 42, 0.04)";
var ROW_BG_HOVER = "rgba(15, 23, 42, 0.07)";
var TEXT_PRIMARY = "#0f172a";
var TEXT_SECONDARY = "rgba(15, 23, 42, 0.65)";
var POSITION_ORDER = {
    GK: 0,
    SW: 1,
    LWB: 2,
    LB: 3,
    LCB: 4,
    CB: 5,
    RCB: 6,
    RB: 7,
    RWB: 8,
    CDM: 10,
    LM: 12,
    LCM: 13,
    CM: 14,
    RCM: 15,
    RM: 16,
    CAM: 20,
    LAM: 21,
    RAM: 22,
    LW: 24,
    RW: 25,
    CF: 28,
    ST: 30,
};
var positionOrder = function (code) {
    return code === null ? 999 : POSITION_ORDER[code];
};
var sortByPosition = function (players) {
    return __spreadArray([], __read(players), false).sort(function (a, b) {
        var pa = positionOrder(a.primaryPosition);
        var pb = positionOrder(b.primaryPosition);
        if (pa !== pb)
            return pa - pb;
        return a.lastName.localeCompare(b.lastName);
    });
};
// Custom bottom sheet portaled to document.body — no MUI Drawer/Modal/Dialog.
// Visually mirrors the desktop Bench rows so users see the same affordances on
// both surfaces.
export var FormationPickerDrawer = function (_a) {
    var _b, _c;
    var open = _a.open, slot = _a.slot, currentPlayer = _a.currentPlayer, benchPlayers = _a.benchPlayers, primaryColour = _a.primaryColour, secondaryColour = _a.secondaryColour, onClose = _a.onClose, onSelectBenchPlayer = _a.onSelectBenchPlayer, onRemoveCurrent = _a.onRemoveCurrent;
    var sorted = useMemo(function () { return sortByPosition(benchPlayers); }, [benchPlayers]);
    var numberTextColour = contrastText(primaryColour);
    if (!open)
        return null;
    return createPortal(_jsxs(Box, { "data-testid": "formation-picker-drawer", children: [_jsx(Box, { onClick: onClose, sx: {
                    position: "fixed",
                    inset: 0,
                    backgroundColor: "rgba(0,0,0,0.5)",
                    zIndex: 1500,
                } }), _jsxs(Box, { sx: {
                    position: "fixed",
                    left: 0,
                    right: 0,
                    bottom: 0,
                    height: "70vh",
                    backgroundColor: "#fff",
                    borderTopLeftRadius: 16,
                    borderTopRightRadius: 16,
                    zIndex: 1501,
                    display: "flex",
                    flexDirection: "column",
                    boxShadow: "0 -4px 12px rgba(0,0,0,0.15)",
                }, children: [_jsxs(Box, { sx: {
                            flexShrink: 0,
                            pt: 1,
                            pb: 1.25,
                            px: 2,
                            borderBottom: "1px solid rgba(15,23,42,0.06)",
                        }, children: [_jsx(Box, { sx: {
                                    width: 36,
                                    height: 4,
                                    backgroundColor: "rgba(15,23,42,0.2)",
                                    borderRadius: 2,
                                    mx: "auto",
                                    mb: 1,
                                } }), _jsxs(Stack, { direction: "row", alignItems: "baseline", spacing: 1, sx: { mb: 0.25 }, children: [_jsx(Typography, { variant: "overline", color: "text.secondary", sx: { lineHeight: 1 }, children: "Position" }), _jsx(Typography, { variant: "h6", component: "h2", sx: { fontWeight: 700, lineHeight: 1, color: TEXT_PRIMARY }, children: (_b = slot === null || slot === void 0 ? void 0 : slot.label) !== null && _b !== void 0 ? _b : "" })] }), _jsx(Typography, { variant: "body2", sx: { color: TEXT_SECONDARY }, children: currentPlayer
                                    ? "Currently: #".concat((_c = currentPlayer.squadNumber) !== null && _c !== void 0 ? _c : "–", " ").concat(currentPlayer.lastName)
                                    : "Choose a player to assign." })] }), _jsx(Box, { sx: {
                            flex: 1,
                            minHeight: 0,
                            overflowY: "auto",
                            WebkitOverflowScrolling: "touch",
                            px: 1.5,
                            py: 1.5,
                        }, children: _jsxs(Stack, { spacing: 0.75, children: [currentPlayer && (_jsxs(Box, { role: "button", "data-testid": "drawer-remove", onClick: onRemoveCurrent, sx: {
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1.5,
                                        px: 1.5,
                                        py: 1.5,
                                        minHeight: 56,
                                        borderRadius: 1,
                                        backgroundColor: "rgba(220, 38, 38, 0.06)",
                                        border: "1px solid rgba(220, 38, 38, 0.20)",
                                        color: "rgb(185, 28, 28)",
                                        cursor: "pointer",
                                        userSelect: "none",
                                        "&:hover": { backgroundColor: "rgba(220, 38, 38, 0.10)" },
                                        "&:active": { backgroundColor: "rgba(220, 38, 38, 0.10)" },
                                    }, children: [_jsx(DeleteOutlineIcon, {}), _jsxs(Typography, { sx: { fontWeight: 600, fontSize: "1rem" }, children: ["Remove ", currentPlayer.lastName, " from pitch"] })] })), sorted.length === 0 && !currentPlayer && (_jsx(Typography, { sx: {
                                        color: TEXT_SECONDARY,
                                        fontStyle: "italic",
                                        fontSize: "0.85rem",
                                        textAlign: "center",
                                        py: 2,
                                    }, children: "No players available" })), sorted.map(function (player) {
                                    var _a;
                                    return (_jsxs(Box, { role: "button", "data-testid": "drawer-player-".concat(player.id), onClick: function () { return onSelectBenchPlayer(player); }, sx: {
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1.25,
                                            pl: 0,
                                            pr: 1.5,
                                            py: 1.25,
                                            minHeight: 56,
                                            borderRadius: 1,
                                            backgroundColor: ROW_BG,
                                            borderLeft: "4px solid ".concat(primaryColour),
                                            color: TEXT_PRIMARY,
                                            cursor: "pointer",
                                            userSelect: "none",
                                            "&:hover": { backgroundColor: ROW_BG_HOVER },
                                            "&:active": { backgroundColor: ROW_BG_HOVER },
                                        }, children: [_jsx(Box, { sx: {
                                                    ml: 1.25,
                                                    width: 36,
                                                    height: 36,
                                                    borderRadius: "50%",
                                                    backgroundColor: primaryColour,
                                                    border: "1.5px solid rgba(15, 23, 42, 0.55)",
                                                    color: numberTextColour,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    fontWeight: 800,
                                                    fontSize: "0.95rem",
                                                    flexShrink: 0,
                                                }, children: (_a = player.squadNumber) !== null && _a !== void 0 ? _a : "-" }), _jsx(Typography, { noWrap: true, sx: {
                                                    flex: 1,
                                                    fontWeight: 600,
                                                    fontSize: "1rem",
                                                }, children: player.lastName }), player.primaryPosition && (_jsx(Box, { sx: {
                                                    px: 1,
                                                    py: 0.5,
                                                    borderRadius: 0.75,
                                                    backgroundColor: "rgba(15, 23, 42, 0.06)",
                                                    border: "1px solid rgba(15, 23, 42, 0.12)",
                                                    color: TEXT_SECONDARY,
                                                    fontSize: "0.75rem",
                                                    fontWeight: 800,
                                                    letterSpacing: 0.5,
                                                    lineHeight: 1,
                                                    flexShrink: 0,
                                                }, children: player.primaryPosition }))] }, player.id));
                                })] }) })] })] }), document.body);
};
